import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetStoreSalesQuery } from "../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../slices/storeIncomesApiSlice";
import { useGetDepositsQuery } from "../slices/depositsApiSlice";
import { useGetStoreSuppliesQuery } from "../slices/storeSuppliesApiSlice";

export default function BalanceByCustomer() {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [storeIncomeBreakdownsByDate, setStoreIncomeBreakdownsByDate] = useState({});

  const { data: storeSalesData } = useGetStoreSalesQuery();
  const { data: storeExpensesData } = useGetStoreExpensesQuery({ startDate, endDate });
  const { data: storeIncomesData } = useGetStoreIncomesQuery({ startDate, endDate });
  const { data: depositsData } = useGetDepositsQuery({ startDate, endDate });
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();

  const getDateRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const filterByDate = (data, date) =>
    data?.filter(item => item.date?.startsWith(date)) || [];

  const groupByCustomerName = (items, key, getName) => {
    return items.reduce((acc, item) => {
      const name = getName(item) || "Unknown";
      acc[name] = (acc[name] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupByUsername = (items, key) => {
    return items.reduce((acc, item) => {
      const username = item.username || "Unknown";
      acc[username] = (acc[username] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupStoreIncomeByCategory = (items) => {
    const grouped = { Morning: 0, Afternoon: 0, Night: 0 };
    items.forEach(item => {
      const category = item.category;
      const amount = Number(item.amount || 0);
      if (grouped.hasOwnProperty(category)) {
        grouped[category] += amount;
      }
    });
    return grouped;
  };

  const calculateSummary = () => {
    const dates = getDateRange(startDate, endDate);
    const summary = {};
    const breakdowns = {};

    dates.forEach(date => {
      const storeSales = groupByCustomerName(
        filterByDate(storeSalesData?.storeSales, date),
        "totalPrice",
        item => item.customerDetails?.name
      );

      const storeExpenses = groupByCustomerName(
        filterByDate(storeExpensesData?.storeExpenses, date),
        "amount",
        item => item.accountName
      );

      const storeIncomes = filterByDate(storeIncomesData?.storeIncomes, date);
      const storeIncomeTotals = groupByUsername(storeIncomes, "amount");

      const deposits = groupByUsername(filterByDate(depositsData?.deposits, date), "amount");
      const storeSupplies = groupByUsername(filterByDate(storeSuppliesData?.storeSupplies, date), "cardCost");

      breakdowns[date] = groupStoreIncomeByCategory(storeIncomes);

      const allNames = new Set([
        ...Object.keys(storeSales),
        ...Object.keys(storeExpenses),
        ...Object.keys(storeIncomeTotals),
        ...Object.keys(deposits),
        ...Object.keys(storeSupplies),
      ]);

      const rows = Array.from(allNames).map(name => ({
        name,
        storeSales: storeSales[name] || 0,
        storeExpenses: storeExpenses[name] || 0,
        storeIncomes: storeIncomeTotals[name] || 0,
        deposits: deposits[name] || 0,
        storeSupplies: storeSupplies[name] || 0,
      }));

      const totals = rows.reduce(
        (acc, row) => {
          acc.storeSales += row.storeSales;
          acc.storeExpenses += row.storeExpenses;
          acc.storeIncomes += row.storeIncomes;
          acc.deposits += row.deposits;
          acc.storeSupplies += row.storeSupplies;
          acc.balance += row.storeIncomes - (row.deposits + row.storeExpenses + row.storeSales) ;
          return acc;
        },
        { storeSales: 0, storeExpenses: 0, storeIncomes: 0, deposits: 0, storeSupplies: 0, balance: 0 }
      );

      summary[date] = { rows, totals };
    });

    setSummaryByDate(summary);
    setStoreIncomeBreakdownsByDate(breakdowns);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(summaryByDate).forEach(([date, { rows, totals }]) => {
      const sheetData = [
        ...rows.map(row => ({
          Date: date,
          Customer: row.name,
          StoreSales: row.storeSales.toFixed(2),
          StoreExpenses: row.storeExpenses.toFixed(2),
          StoreIncomes: row.storeIncomes.toFixed(2),
          Deposits: row.deposits.toFixed(2),
          StoreSupplies: row.storeSupplies.toFixed(2),
        })),
        {
          Date: date,
          Customer: "TOTAL",
          StoreSales: totals.storeSales.toFixed(2),
          StoreExpenses: totals.storeExpenses.toFixed(2),
          StoreIncomes: totals.storeIncomes.toFixed(2),
          Deposits: totals.deposits.toFixed(2),
          StoreSupplies: totals.storeSupplies.toFixed(2),
        },
      ];
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, date);
    });

    Object.entries(storeIncomeBreakdownsByDate).forEach(([date, breakdown]) => {
      const sheetData = Object.entries(breakdown).map(([category, amount]) => ({
        Category: category,
        Amount: amount.toFixed(2),
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `${date}_StoreIncomeBreakdown`);
    });

    XLSX.writeFile(workbook, `CustomerSummary_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="range-customer-summary">
      <h2>Summary by Customer and Date</h2>
      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
      </div>
      <div className="actions">
        <button onClick={calculateSummary}>Generate Summary</button>
        <button onClick={exportToExcel} disabled={Object.keys(summaryByDate).length === 0}>
          Export to Excel
        </button>
      </div>

      {Object.entries(summaryByDate).map(([date, { rows, totals }]) => (
        <div key={date} className="daily-summary">
          <h3>{date}</h3>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>StoreSales</th>
                <th>Inc/Exp</th>
                <th>NetCash</th>
                <th>CashCollected</th>
                <th style={{ display: 'none' }}>Deposits</th>
                <th style={{ display: 'none' }}>StoreSupplies</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{row.storeSales.toFixed(2)}</td>
                  <td>{row.storeExpenses.toFixed(2)}</td>
                  <td>{(row.storeSales - row.deposits + row.storeExpenses ).toFixed(2)}</td>
                  <td>{row.storeIncomes.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.deposits.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.storeSupplies.toFixed(2)}</td>
                  <td>{(row.storeSales - row.storeIncomes + row.deposits + row.storeExpenses ).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td>{totals.storeSales.toFixed(2)}</td>
                <td>{totals.storeExpenses.toFixed(2)}</td>
                <td>{(totals.storeSales + totals.storeExpenses).toFixed(2)}</td>
                <td>{totals.storeIncomes.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.deposits.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.storeSupplies.toFixed(2)}</td>
                <td>{totals.balance.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <h4>StoreIncome Breakdown</h4>
<table>
  <thead>
    <tr>
      <th>Category</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {Object.entries(storeIncomeBreakdownsByDate[date] || {}).map(([category, amount]) => (
      <tr key={category}>
        <td>{category}</td>
        <td>{amount.toFixed(2)}</td>
      </tr>
    ))}
    <tr className="totals-row">
      <td><strong>Total</strong></td>
      <td>
        {Object.values(storeIncomeBreakdownsByDate[date] || {})
          .reduce((sum, val) => sum + val, 0)
          .toFixed(2)}
      </td>
    </tr>
  </tbody>
</table>
        </div>
      ))}
    </div>
  );
}